import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Box, Button, Card, CardContent, CardHeader, Grid, Stack, Typography } from '@mui/material';
import { getUserAvailableFactors, getUserEnrolledFactors } from 'api/requests/userApi';
import { transformUsername } from 'utils/informationUtils';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from './Security.module.scss';
import { useTranslation } from 'react-i18next';
import { useUserData } from 'hooks/hooks';
import { logger } from 'utils/logger';
import { EXTRA_VERIFICATION_METHOD_IDS } from './extraVerificationMethods';
export const classifyFactor = (provider, vendorName, subType) => {
    const normalizedSubType = typeof subType === 'string' ? subType.toLowerCase() : undefined;
    const vendor = vendorName === null || vendorName === void 0 ? void 0 : vendorName.toUpperCase();
    const prov = provider === null || provider === void 0 ? void 0 : provider.toUpperCase();
    if (normalizedSubType === 'email') {
        return 'email';
    }
    if (normalizedSubType === 'sms') {
        return 'sms';
    }
    const isTotp = (normalizedSubType === null || normalizedSubType === void 0 ? void 0 : normalizedSubType.includes('token')) && (normalizedSubType === null || normalizedSubType === void 0 ? void 0 : normalizedSubType.includes('totp'));
    if (normalizedSubType === 'push' || isTotp) {
        if (vendor === 'OKTA' || prov === 'OKTA') {
            return 'oktaVerify';
        }
        if ((vendor === null || vendor === void 0 ? void 0 : vendor.includes('GOOGLE')) || prov === 'GOOGLE') {
            return 'googleAuth';
        }
    }
    return undefined;
};
export const Security = () => {
    const { t } = useTranslation('security');
    const { profile } = useUserData();
    const usernameField = transformUsername(profile.username);
    usernameField.label = t('primarySection.usernameLabel');
    const [extraVerificationMethods, setExtraVerificationMethods] = React.useState(() => EXTRA_VERIFICATION_METHOD_IDS.map((id) => ({ id, status: 'not_enrolled' })));
    React.useEffect(() => {
        Promise.all([getUserEnrolledFactors(), getUserAvailableFactors()])
            .then(([enrolledResponse, availableResponse]) => {
            var _a, _b;
            const enrolledFactors = (_a = enrolledResponse.data.factors) !== null && _a !== void 0 ? _a : [];
            const availableFactors = (_b = availableResponse.data.factors) !== null && _b !== void 0 ? _b : [];
            const enrolledIds = new Set();
            enrolledFactors.forEach((factor) => {
                const profile = factor.profile;
                const subType = typeof (profile === null || profile === void 0 ? void 0 : profile.subType) === 'string' ? profile.subType : undefined;
                const id = classifyFactor(factor.provider, factor.vendorName, subType);
                if (id) {
                    enrolledIds.add(id);
                }
            });
            const availableIds = new Set();
            availableFactors.forEach((factor) => {
                var _a;
                const subType = (_a = factor.profile) === null || _a === void 0 ? void 0 : _a.subType;
                const id = classifyFactor(factor.provider, factor.vendorName, subType);
                if (id) {
                    availableIds.add(id);
                }
            });
            const methods = EXTRA_VERIFICATION_METHOD_IDS.filter((id) => enrolledIds.has(id) || availableIds.has(id)).map((id) => ({
                id,
                status: enrolledIds.has(id) ? 'enabled' : 'not_enrolled'
            }));
            setExtraVerificationMethods(methods);
            logger.info('Security page: loaded extra verification methods', {
                enrolledCount: enrolledIds.size,
                availableCount: availableIds.size,
                enrolledMethods: Array.from(enrolledIds),
                availableMethods: Array.from(availableIds),
                methods: methods.map((method) => ({ id: method.id, status: method.status }))
            });
        })
            .catch((error) => {
            logger.error('Error fetching factors', { error });
        });
    }, []);
    return (_jsxs(Grid, { container: true, spacing: 4, children: [_jsx(Grid, { size: { xs: 12, md: 12 }, children: _jsxs(Card, { className: styles.sectionCard, children: [_jsx(CardHeader, { title: t('primarySection.title'), className: styles.sectionHeader, titleTypographyProps: { variant: 'subtitle1' } }), _jsxs(CardContent, { className: styles.sectionContent, children: [_jsxs(Box, { mt: 2, children: [_jsx(Typography, { className: styles.fieldLabel, children: usernameField.label }), _jsx(Typography, { className: styles.fieldValue, children: usernameField.value })] }), _jsx(Box, { mt: 2, children: _jsx(Typography, { className: styles.fieldLabel, children: t('primarySection.passwordLabel') }) }), _jsx(Button, { variant: "contained", color: "primary", className: styles.changePasswordButton, disabled: true, onClick: () => { }, children: t('primarySection.button') })] })] }) }), _jsx(Grid, { size: { xs: 12, md: 12 }, children: _jsxs(Card, { className: styles.sectionCard, children: [_jsx(CardHeader, { title: t('secondarySection.title'), className: styles.sectionHeader, titleTypographyProps: { variant: 'subtitle1' } }), _jsxs(CardContent, { className: styles.sectionContent, children: [_jsx(Typography, { variant: "body2", className: styles.description, children: t('secondarySection.description') }), _jsx(Stack, { spacing: 1.5, className: styles.extraList, children: extraVerificationMethods.map((method) => (_jsxs(Box, { className: styles.extraRow, children: [_jsx(Typography, { className: styles.extraMethodName, children: t(`secondarySection.methods.${method.id}`) }), method.status === 'enabled' ? (_jsx(FontAwesomeIcon, { icon: faCheckCircle, color: "#16a34a" })) : (_jsx(Box, { className: `${styles.statusPill} ${styles.statusPillNotEnrolled}`, children: t('secondarySection.notEnrolled') }))] }, method.id))) })] })] }) })] }));
};
